import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import Paragraph from '../../Paragraph/Paragraph';
import SectionLoader from '../../Loader/SectionLoader';
import RowItemElement from '../../Buy/RowItem';
import SelectPaymentOptions from '../../Buy/SelectPaymentOptions';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import SellFromHomeQuantityStepper from './SellFromHomeQuantityStepper';
import SellFromHomePriceGuaranteeCard from './SellFromHomePriceGuaranteeCard';
import SellFromHomePreAuthDrawer from './SellFromHomePreAuthDrawer';
import SellFromHomeIrsSsnDrawer from './SellFromHomeIrsSsnDrawer';
import {
  findDefaultPaymentMethod,
  formatPaymentMethodLabel,
  mapCreditCardsToPaymentMethods,
} from './sellFromHomePaymentHelpers';
import {
  resolveSfhVaultSymbol,
  SFH_VAULT_LOCATION,
} from './sellFromHomeVaultHelpers';
import { ADD_ADDRESS_WIZARD } from '../../../constants/sessionStorage';
import { removeFromSessionStorage } from '../../../util/helpers/sessionStorageHelper';
import {
  fetchProductSymbolPrices,
  fetchProductTiers,
} from '../../../store/actions/products/productActions';
import { fetchCreditCards } from '../../../store/actions/creditCard/creditCardActions';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import { selectCreditCards } from '../../../store/selectors/creditCardSelectors';
import {
  getCurrentAccountUid,
  getCurrentAccount,
  selectAccountAddresses,
  selectDefaultMailingAddress,
  selectHasFullTaxIdOnFile,
  selectPrimaryAccountHolderDisplayName,
} from '../../../store/selectors/accountSelectors';
import {
  fetchAccountAddresses,
  fetchAccountUsers,
} from '../../../store/actions/account/accountActions';
import { getQuotesForOrderSellFromHome } from '../../../store/actions/orders/orderActions';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import {
  PRODUCT_SYMBOL_PRICES_LOADING,
  PRODUCT_TIERS_LOADING,
} from '../../../store/actions/products/productActionConstants';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as SfhCreditCard } from '../../../assets/images/svg/sfh-credit-card.svg';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { accountTypeKeys } from '../../../util/enum/api/accountTypes';
import { GS_IRS_LINK } from '../../../util/constants';
import { buildSfhIrsDrawerCopy } from '../../../constants/sfhIrsCopy';
import { resolveSfhSellTaxComplianceGate } from '../../../services/sfhSellTaxComplianceGate';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-top: 8px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const ProductTitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 1.3;
  color: ${themeColors.colorTextPrimary};
`;

const ProductWeight = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const UnitPrice = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  text-align: right;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
`;

const EmptyHint = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  background: ${themeColors.colorPrimaryLight};
  border-radius: 4px;
  border: 1px solid ${themeColors.colorCardBorder};
  padding: 12px 16px;
`;

const UnitsTotal = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  background: ${themeColors.colorPrimaryLight};
  border-radius: 4px;
  border: 1px solid ${themeColors.colorCardBorder};
  padding: 12px 16px;

  > b {
    color: ${themeColors.colorTextPrimary};
    font-weight: 600;
  }

  > span {
    color: ${themeColors.colorBlack};
    font-weight: 600;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 64px;
`;

const OverLimitSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 12px;
`;

const WarningPill = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 6px;
  padding: 12px 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }
`;

const NeedHelpLink = styled.a`
  display: block;
  text-align: center;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorPrimary};
  text-decoration: underline;
`;

const Spacer = styled.div`
  flex: 1;
  min-height: 16px;
`;

// Shown above the CTA only when the payment row is absent (quantity 0).
// Once the payment row renders, its own top border serves as the divider.
const FooterDivider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  margin: 0 -16px 16px;
`;

const Footer = styled.div`
  > button {
    width: 100%;
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1;
    color: ${themeColors.colorGrey};
  }

  /* Figma disabled state: solid grey (colorGrey3 #ADB1C7) at full opacity,
     not the default translucent primary. :hover beats the primary variant's
     own :disabled:hover rule. */
  > button:disabled,
  > button:disabled:hover {
    background: ${themeColors.colorGrey3};
    border-color: ${themeColors.colorGrey3};
    opacity: 1;
  }
`;

const ORDER_LIMIT = 200000;
const PRICE_POLL_INTERVAL_MS = 30_000;

const SellFromHomeQuantity = ({
  selectedHit,
  quantity,
  setQuantity,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  handleNext,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const hasFullTaxIdOnFile = useSelector(selectHasFullTaxIdOnFile);
  const primaryHolderDisplayName = useSelector(
    selectPrimaryAccountHolderDisplayName,
  );
  const productBySymbol = useSelector(getProductBySymbol);
  const creditCards = useSelector(selectCreditCards);
  const isLoadingPrice = useSelector(
    selectIsLoadingByActionTypes([
      PRODUCT_SYMBOL_PRICES_LOADING,
      PRODUCT_TIERS_LOADING,
    ]),
  );
  const [isPreAuthOpen, setIsPreAuthOpen] = useState(false);
  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);
  const [isIrsDrawerOpen, setIsIrsDrawerOpen] = useState(false);
  const [tierPricePerUnit, setTierPricePerUnit] = useState(null);
  const [hasTouchedQuantity, setHasTouchedQuantity] = useState(false);
  const [isLoadingGetQuotes, setIsLoadingGetQuotes] = useState(false);
  const [getQuotesError, setGetQuotesError] = useState('');

  useEffect(() => {
    if (quantity > 0 && !hasTouchedQuantity) setHasTouchedQuantity(true);
  }, [quantity, hasTouchedQuantity]);

  const productCode = selectedHit?.Code;
  const vaultSymbol = useMemo(
    () => resolveSfhVaultSymbol(selectedHit, account),
    [selectedHit, account],
  );

  useEffect(() => {
    if (!accountUid || !vaultSymbol || hasTouchedQuantity) return undefined;
    const dispatchFetch = () => {
      dispatch(
        fetchProductSymbolPrices({
          accountUid,
          symbol: vaultSymbol,
          side: 'sell',
        }),
      );
    };
    dispatchFetch();
    const intervalId = setInterval(dispatchFetch, PRICE_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [accountUid, vaultSymbol, hasTouchedQuantity, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchCreditCards({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchAccountUsers({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setQuantity((q) => {
          const max = maxQuantityRef.current;
          return max != null ? Math.min(max, q + 1) : q + 1;
        });
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setQuantity((q) => Math.max(0, q - 1));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [setQuantity]);

  const unitPrice = hasTouchedQuantity
    ? Number(tierPricePerUnit) || 0
    : Number(productBySymbol?.ProductPrice) || 0;
  const hasUnitPrice = unitPrice > 0;

  const maxQuantity = hasUnitPrice
    ? Math.floor(ORDER_LIMIT / unitPrice) + 1
    : null;
  const maxQuantityRef = useRef(maxQuantity);
  maxQuantityRef.current = maxQuantity;

  const creditCardPaymentMethods = useMemo(
    () => mapCreditCardsToPaymentMethods(creditCards),
    [creditCards],
  );

  const defaultPaymentMethod = useMemo(
    () => findDefaultPaymentMethod(creditCardPaymentMethods),
    [creditCardPaymentMethods],
  );

  useEffect(() => {
    if (!selectedPaymentMethod && defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod, setSelectedPaymentMethod]);

  const activePaymentMethod = selectedPaymentMethod || defaultPaymentMethod;

  useEffect(() => {
    // Freeze the price refresh once the user commits via Continue to Shipping
    // (get-quotes in flight) or the IRS drawer opens — the price they agreed
    // to is locked. Skip the reset + poll until they return to this screen.
    if (isIrsDrawerOpen || isLoadingGetQuotes) return undefined;
    setTierPricePerUnit(null);
    if (!accountUid || !vaultSymbol || !hasTouchedQuantity) return undefined;
    const dispatchFetch = () => {
      dispatch(
        fetchProductTiers({
          accountUid,
          symbol: vaultSymbol,
          side: 'Sell',
          location: SFH_VAULT_LOCATION,
          trueTiers: true,
          onSuccess: (data) => {
            const firstTierUnitPrice = Number(data?.Tiers?.[0]?.PricePerUnit);
            setTierPricePerUnit(
              Number.isFinite(firstTierUnitPrice) ? firstTierUnitPrice : null,
            );
          },
        }),
      );
    };
    dispatchFetch();
    const intervalId = setInterval(dispatchFetch, PRICE_POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [
    accountUid,
    vaultSymbol,
    hasTouchedQuantity,
    isIrsDrawerOpen,
    isLoadingGetQuotes,
    dispatch,
  ]);

  const backendOrderTotal = hasUnitPrice ? unitPrice * quantity : 0;
  const hasBackendOrderTotal = quantity > 0 && backendOrderTotal > 0;
  const isOverLimit = hasUnitPrice && unitPrice * quantity > ORDER_LIMIT;
  const supportPhone = t('sellWizard.sfhWizard.phone');

  const accountType = parseEnumType(accountTypeKeys, account?.AccountType);

  const irdCopy = useMemo(
    () =>
      buildSfhIrsDrawerCopy({
        t,
        accountType,
        primaryHolderDisplayName,
        learnMoreHref: GS_IRS_LINK,
      }),
    [t, accountType, primaryHolderDisplayName],
  );

  const isSegregated = Boolean(productBySymbol?.IsSegregated);

  const addressUid =
    defaultMailingAddress?.AddressUid ||
    accountAddresses?.find((a) => a?.IsVerified)?.AddressUid ||
    accountAddresses?.[0]?.AddressUid;

  const handleGetQuotesSuccess = (getQuoteResult) => {
    const { requiresSsnUi } = resolveSfhSellTaxComplianceGate({
      irsReportingRequired:
        getQuoteResult?.QuoteStatusItem?.IRSReportingRequired,
      hasFullTaxIdOnFile,
    });

    if (requiresSsnUi) {
      setIsIrsDrawerOpen(true);
      return;
    }
    handleNext();
  };

  const handleGetQuotesError = (error) => {
    const errorCode = getErrorCodeFromAPIResponse(error);
    setGetQuotesError(
      errorCode ? t(`apiErrors.${errorCode}`) : t('common.error'),
    );
  };

  const handleContinue = () => {
    if (
      !productCode ||
      !accountUid ||
      !vaultSymbol ||
      !handleNext ||
      !activePaymentMethod ||
      !addressUid
    ) {
      return;
    }

    setGetQuotesError('');
    setIsLoadingGetQuotes(true);
    dispatch(
      getQuotesForOrderSellFromHome({
        getQuotesPayload: {
          accountUid,
          symbolCode: vaultSymbol,
          quantityOrAmount: quantity,
          sideType: 'Sell',
          unitType: 'Quantity',
          paymentType: activePaymentMethod?.PaymentMethodType,
          paymentTypeUid: activePaymentMethod?.PaymentMethodUid,
          addressUid,
          isSegregated,
          addPaymentMethodData: false,
          orderEntryType: 'OrderEntry',
          isSellFromHome: true,
        },
        isLoading: setIsLoadingGetQuotes,
        onSuccess: handleGetQuotesSuccess,
        onError: handleGetQuotesError,
      }),
    );
  };

  const handleIrsDrawerSuccess = () => {
    setIsIrsDrawerOpen(false);
    handleNext();
  };

  const handleIrsLockedOut = () => {
    setIsIrsDrawerOpen(false);
    if (typeof handleClose === 'function') {
      handleClose();
    }
  };

  return (
    <SectionLoader isLoading={isLoadingPrice || isLoadingGetQuotes}>
      <ModalBody>
        <Header>
          <HeaderLeft>
            <ProductTitle data-cy="text-sfh-product-title">
              {selectedHit?.Caption}
            </ProductTitle>
            <ProductWeight>{selectedHit?.WeightCaption}</ProductWeight>
          </HeaderLeft>
          {hasUnitPrice && (
            <UnitPrice data-cy="text-sfh-unit-price">
              {formatMoneyNumeral(unitPrice)}
            </UnitPrice>
          )}
        </Header>

        <SellFromHomeQuantityStepper
          value={quantity}
          onChange={setQuantity}
          max={maxQuantity}
        />

        <Body>
          {quantity === 0 ? (
            <EmptyHint data-cy="text-sfh-select-quantity">
              {t('sellWizard.sfhWizard.quantity.selectQuantity')}
            </EmptyHint>
          ) : (
            <>
              <UnitsTotal data-cy="text-sfh-units-total">
                {t('sellWizard.sfhWizard.quantity.unitsLabel', {
                  count: quantity,
                })}
                {hasBackendOrderTotal && (
                  <>
                    {' = '}
                    <span>{formatMoneyNumeral(backendOrderTotal)}</span>
                  </>
                )}
              </UnitsTotal>
              {!isOverLimit && hasUnitPrice && (
                <SellFromHomePriceGuaranteeCard
                  price={unitPrice}
                  onLearnMore={() => setIsPreAuthOpen(true)}
                />
              )}
            </>
          )}
        </Body>

        <Spacer />

        {quantity > 0 && isOverLimit && (
          <OverLimitSection>
            <WarningPill data-cy="text-sfh-order-limit-warning">
              <AmberAlertTriangle />
              {t('sellWizard.sfhWizard.quantity.orderLimitWarning')}
            </WarningPill>
            <NeedHelpLink href={`tel:${supportPhone.replace(/[^\d+]/g, '')}`}>
              {t('sellWizard.sfhWizard.needHelp')} {supportPhone}
            </NeedHelpLink>
          </OverLimitSection>
        )}

        {quantity > 0 && activePaymentMethod && (
          <RowItemElement
            label={t('sellWizard.sfhWizard.quantity.payWith')}
            value={
              <>
                <SfhCreditCard />
                {formatPaymentMethodLabel(activePaymentMethod, t)}
              </>
            }
            clickLabel={t('sellWizard.sfhWizard.quantity.change')}
            onClick={() => setIsPaymentDrawerOpen(true)}
          />
        )}

        {quantity > 0 && !activePaymentMethod && (
          <RowItemElement
            label={t('sellWizard.sfhWizard.quantity.payWith')}
            value={t('sellWizard.sfhWizard.quantity.addCreditCard')}
            onClickValue={() => setIsAddPaymentMethodOpen(true)}
            isNotButton
          />
        )}

        {getQuotesError && (
          <Paragraph isError fontSize={14} marginTop={12}>
            {getQuotesError}
          </Paragraph>
        )}

        <Footer>
          {quantity === 0 && <FooterDivider />}
          <Button
            variant="primary"
            textTransform="none"
            disabled={
              quantity === 0 ||
              isOverLimit ||
              !activePaymentMethod ||
              !addressUid ||
              !hasUnitPrice ||
              !hasBackendOrderTotal ||
              isLoadingGetQuotes
            }
            onClick={handleContinue}
            data-cy="button-sfh-continue-shipping"
          >
            {t('sellWizard.sfhWizard.quantity.continueToShipping')}
          </Button>
        </Footer>
      </ModalBody>
      <SellFromHomePreAuthDrawer
        isOpen={isPreAuthOpen}
        onClose={() => setIsPreAuthOpen(false)}
      />
      <SelectPaymentOptions
        open={isPaymentDrawerOpen}
        setOpen={setIsPaymentDrawerOpen}
        paymentMethods={creditCardPaymentMethods}
        selectedPaymentMethod={activePaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        accountUid={accountUid}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        wizardData={{}}
        setWizardData={() => {}}
        setSuccessToast={() => {}}
        creditCardOnly
      />
      {isAddAddressOpen && (
        <AddDeliveryAddressModal
          isOpen={isAddAddressOpen}
          onClose={() => {
            removeFromSessionStorage(ADD_ADDRESS_WIZARD);
            setIsAddAddressOpen(false);
          }}
          title={t('buyWizard.addYourDeliveryAddress')}
          page="address"
          avoidClearingStorage
          isAddAddressOpen={isAddAddressOpen}
          setIsAddAddressOpen={setIsAddAddressOpen}
        />
      )}
      <SellFromHomeIrsSsnDrawer
        isOpen={isIrsDrawerOpen}
        onClose={() => setIsIrsDrawerOpen(false)}
        onSuccess={handleIrsDrawerSuccess}
        onLockedOut={handleIrsLockedOut}
        accountUid={accountUid}
        copy={irdCopy}
      />
    </SectionLoader>
  );
};

SellFromHomeQuantity.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  quantity: PropTypes.number,
  setQuantity: PropTypes.func,
  selectedPaymentMethod: PropTypes.shape({}),
  setSelectedPaymentMethod: PropTypes.func,
  handleNext: PropTypes.func,
  handleClose: PropTypes.func,
};

export default SellFromHomeQuantity;
